import { api, enc } from "@/lib/api";
import { extractScore } from "@/components/ResultScore";
import { recordScore } from "@/lib/atsScores";

const KEY = "rpopro_tailor_map";
const readMap = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const HINT = /suggest|tip|improv|fix|missing|keyword|change|edit|rewrite|recommend|gap|action|bullet/i;
const OPT_KEY = /(tailored|optimi[sz]ed|rewritten|improved|new|updated|enhanced)_?resume|resume_?(text|draft|rewrite)/i;
const SUM_KEY = /(optimi[sz]ed|tailored|improved|rewritten).*summary|summary.*(optimi[sz]ed|tailored)/i;
const BUL_KEY = /(optimi[sz]ed|tailored|improved|rewritten).*bullet|bullet.*(optimi[sz]ed|tailored)/i;
const PARTS = /summary|bullet/i;
const toLines = (v) => (Array.isArray(v) ? v.flatMap(toLines) : v && typeof v === "object" ? toLines(v.text || v.bullet || v.after || v.rewrite || Object.values(v).find((x) => typeof x === "string") || "") : String(v || "").split(/\n+/))
  .map((s) => s.replace(/^\s*([•\-*▪◦]|\d+[.)])\s*/, "").trim()).filter((s) => s.length > 3);

export const boostContentOf = (v, out = { summary: null, bullets: [] }, depth = 0) => {
  if (!v || typeof v !== "object" || depth > 4) return out;
  Object.entries(v).forEach(([k, x]) => {
    if (SUM_KEY.test(k) && !out.summary) out.summary = toLines(x).join(" ") || null;
    else if (BUL_KEY.test(k)) out.bullets.push(...toLines(x));
    else if (x && typeof x === "object") boostContentOf(x, out, depth + 1);
  });
  out.bullets = [...new Set(out.bullets)].slice(0, 20);
  return out;
};
export const hasContent = (c) => !!(c && (c.summary || c.bullets?.length));

export const collectIdeas = (v, out = [], hinted = false, depth = 0, key = "") => {
  if (depth > 5 || out.length >= 20 || v == null || OPT_KEY.test(key) || SUM_KEY.test(key) || BUL_KEY.test(key)) return out;
  if (typeof v === "string") { const t = v.trim(); if (hinted && t.length > 1) out.push(/keyword|skill/i.test(key) && t.split(/\s+/).length <= 4 ? `Add keyword: ${t}` : t); return out; }
  if (Array.isArray(v)) { v.forEach((x) => collectIdeas(x, out, hinted, depth + 1, key)); return out; }
  if (typeof v === "object") {
    const flat = ["text", "tip", "suggestion", "title", "change", "issue"].map((k) => v[k]).find((x) => typeof x === "string");
    if (hinted && flat) { out.push([flat, v.fix || v.detail || v.why].filter((x) => typeof x === "string").join(" — ")); return out; }
    Object.entries(v).forEach(([k, x]) => collectIdeas(x, out, hinted || HINT.test(k), depth + 1, k));
  }
  return out;
};
export const ideasOf = (res) => [...new Set(collectIdeas(res))].slice(0, 15);

export const optimizedOf = (v, depth = 0) => {
  if (!v || typeof v !== "object" || depth > 4) return null;
  for (const [k, x] of Object.entries(v)) {
    if (OPT_KEY.test(k) && !PARTS.test(k)) {
      if (typeof x === "string" && x.trim().length > 200) return { text: x.trim() };
      if (x && typeof x === "object" && (x.experience || x.summary)) return { resume: x };
    }
  }
  for (const x of Object.values(v)) { const f = optimizedOf(x, depth + 1); if (f) return f; }
  return null;
};

export const stashTailor = (t) => {
  const prev = readTailor(t.jobId);
  const keep = prev && {
    ideas: t.ideas?.length ? t.ideas : prev.ideas,
    content: hasContent(t.content) ? t.content : prev.content,
    toolTitle: t.toolTitle || prev.toolTitle,
    job: t.job?.title ? t.job : prev.job,
  };
  const map = readMap();
  map[t.jobId || "_"] = { ...t, ...(keep || {}), at: Date.now() };
  const top = Object.entries(map).sort((a, b) => b[1].at - a[1].at).slice(0, 30);
  localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(top)));
};
export const readTailor = (jobId) => readMap()[jobId || "_"] || null;
export const clearOptimized = (jobId) => { const t = readTailor(jobId); if (t) { const map = readMap(); map[jobId || "_"] = { ...t, optimized: null }; localStorage.setItem(KEY, JSON.stringify(map)); } };
export const tailorUrl = (jobId) => `/resume-studio${jobId ? `?job=${encodeURIComponent(jobId)}` : ""}`;

export const loadJob = (jobId) => api.get(`/jobs/${enc(jobId)}`).then((r) => r.data?.job || r.data).catch(() => null);

export const runAts = async (tool, jobId) => {
  const { data } = await api.post(`/ai/tools/${tool.id}`, { job_id: jobId, inputs: {} });
  const s = extractScore(data, true);
  if (s != null) recordScore(jobId, s, tool.id);
  return s;
};
